import { useEffect, useMemo, useState } from 'react'
import { registry, PART_META, type Part, type Unit } from '../lib/registry'

/**
 * The Voltron.
 *
 * Two modes, and the second is the point of the whole app:
 *
 *   THE FACTORY  — every piece we own, lit by how much of each limb is built.
 *   A UNIT       — the pieces THAT unit is assembled from, with anything it
 *                  requires and we do not have burning red.
 *
 * The parts are grouped by what they are FOR rather than alphabetically,
 * because a limb that fails together should be drawn together. Money and reach
 * are different arms: one loses you a sale, the other loses you a customer.
 */

type Mode = { kind: 'factory' } | { kind: 'unit'; unit: Unit }

export default function Voltron() {
  const [parts, setParts] = useState<Part[]>([])
  const [units, setUnits] = useState<Unit[]>([])
  const [mode, setMode] = useState<Mode>({ kind: 'factory' })
  const [focus, setFocus] = useState<string | null>(null)

  useEffect(() => {
    void Promise.all([registry.components(), registry.units()]).then(([c, u]) => {
      setParts(c.parts)
      // Only things actually going somewhere are worth assembling.
      setUnits(u.units.filter((x) => ['building', 'planned', 'idea'].includes(x.lifecycle)))
    })
  }, [])

  /** For each part: how lit it is, and what is wrong with it. */
  const state = useMemo(() => {
    const out: Record<string, { readiness: number; needed: number; missing: string[]; label: string }> = {}
    for (const p of parts) {
      if (mode.kind === 'factory') {
        out[p.part] = {
          readiness: p.readiness,
          needed: p.total,
          missing: p.components.filter((c) => c.status !== 'built').map((c) => c.name),
          label: `${p.built}/${p.total}`,
        }
      } else {
        const wanted = mode.unit.components.filter((c) => c.part === p.part)
        const required = wanted.filter((c) => c.need === 'required')
        const have = required.filter((c) => c.status === 'built')
        out[p.part] = {
          readiness: required.length ? have.length / required.length : wanted.length ? 1 : -1,
          needed: wanted.length,
          missing: required.filter((c) => c.status !== 'built').map((c) => c.name),
          label: required.length ? `${have.length}/${required.length}` : wanted.length ? '—' : '',
        }
      }
    }
    return out
  }, [parts, mode])

  const focused = parts.find((p) => p.part === focus)

  return (
    <section className="voltron">
      <header className="voltron-head">
        <div>
          <h1>Voltron</h1>
          <p className="muted">
            {mode.kind === 'factory'
              ? 'Every piece the factory owns. A limb is only whole when all of it is built.'
              : `What ${mode.unit.name} is assembled from. Red is required and missing.`}
          </p>
        </div>
        <label className="assemble">
          Assemble for
          <select
            value={mode.kind === 'unit' ? mode.unit.slug : ''}
            onChange={(e) => {
              const unit = units.find((u) => u.slug === e.target.value)
              setMode(unit ? { kind: 'unit', unit } : { kind: 'factory' })
              setFocus(null)
            }}
          >
            <option value="">the whole factory</option>
            {units.map((u) => <option key={u.slug} value={u.slug}>{u.name}</option>)}
          </select>
        </label>
      </header>

      <div className="voltron-body">
        <svg viewBox="0 0 300 400" className="robot" role="img" aria-label="The Voltron of available components">
          <defs>
            <linearGradient id="lit" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--brand)" stopOpacity="0.95" />
              <stop offset="100%" stopColor="var(--brand)" stopOpacity="0.55" />
            </linearGradient>
          </defs>
          {PART_SHAPES.map(({ part, d, cx, cy }) => {
            const s = state[part]
            const missing = (s?.missing.length ?? 0) > 0
            const unused = s?.readiness === -1
            const ready = (s?.readiness ?? 0) >= 1 && !unused
            return (
              <g
                key={part}
                className={[
                  'part',
                  ready ? 'is-ready' : '',
                  unused ? 'is-unused' : '',
                  missing && mode.kind === 'unit' ? 'is-blocked' : '',
                  focus === part ? 'is-focused' : '',
                ].join(' ')}
                onClick={() => setFocus(focus === part ? null : part)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setFocus(focus === part ? null : part) }}
              >
                <path d={d} />
                {/* The fraction, not a percentage — 3/5 says more than 60%. */}
                <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle">{s?.label ?? ''}</text>
              </g>
            )
          })}
        </svg>

        <aside className="voltron-side">
          {!focused && (
            <>
              <h2>{mode.kind === 'factory' ? 'The whole thing' : mode.unit.name}</h2>
              {mode.kind === 'unit' && <p className="muted">{mode.unit.intent ?? mode.unit.tagline}</p>}
              {mode.kind === 'unit' && mode.unit.blockers.length > 0 && (
                <p className="blocked-note">
                  Blocked on {mode.unit.blockers.join(', ')}.
                </p>
              )}
              <ul className="part-list">
                {parts.map((p) => {
                  const s = state[p.part]
                  return (
                    <li key={p.part} onClick={() => setFocus(p.part)}>
                      <span>{PART_META[p.part]?.label}</span>
                      <span className={s?.missing.length ? 'muted warnish' : 'muted'}>{s?.label}</span>
                    </li>
                  )
                })}
              </ul>
              <p className="muted small">Tap a limb.</p>
            </>
          )}

          {focused && (
            <>
              <button className="link" onClick={() => setFocus(null)}>← all limbs</button>
              <h2>{PART_META[focused.part]?.label}</h2>
              <p className="muted small">{PART_META[focused.part]?.role}</p>
              <ul className="piece-list">
                {focused.components.map((c) => {
                  const inUnit = mode.kind === 'unit'
                    ? mode.unit.components.find((x) => x.component_id === c.id)
                    : null
                  const dimmed = mode.kind === 'unit' && !inUnit
                  return (
                    <li key={c.id} className={dimmed ? 'dim' : ''}>
                      <div className="piece-top">
                        <strong>{c.name}</strong>
                        <span className={`chip chip-${c.status}`}>{c.status}</span>
                        {inUnit && <span className={`chip chip-${inUnit.need}`}>{inUnit.need}</span>}
                      </div>
                      <p className="muted small">{c.summary}</p>
                      {inUnit?.note && <p className="muted small note">“{inUnit.note}”</p>}
                      <p className="muted small mono">
                        {c.location ?? 'not built yet'}{c.origin ? ` · from ${c.origin}` : ''}
                      </p>
                    </li>
                  )
                })}
              </ul>
            </>
          )}
        </aside>
      </div>
    </section>
  )
}

/* Blocky on purpose: this is a machine assembled from parts, not a character. */
const PART_SHAPES = [
  { part: 'head',       d: 'M112 18 h76 a10 10 0 0 1 10 10 v52 a10 10 0 0 1 -10 10 h-76 a10 10 0 0 1 -10 -10 v-52 a10 10 0 0 1 10 -10 z', cx: 150, cy: 54 },
  { part: 'torso',      d: 'M98 100 h104 a10 10 0 0 1 10 10 v126 a10 10 0 0 1 -10 10 h-104 a10 10 0 0 1 -10 -10 v-126 a10 10 0 0 1 10 -10 z', cx: 150, cy: 173 },
  { part: 'right-arm',  d: 'M28 112 h44 a10 10 0 0 1 10 10 v122 a10 10 0 0 1 -10 10 h-44 a10 10 0 0 1 -10 -10 v-122 a10 10 0 0 1 10 -10 z', cx: 50, cy: 183 },
  { part: 'left-arm',   d: 'M228 112 h44 a10 10 0 0 1 10 10 v122 a10 10 0 0 1 -10 10 h-44 a10 10 0 0 1 -10 -10 v-122 a10 10 0 0 1 10 -10 z', cx: 250, cy: 183 },
  { part: 'right-leg',  d: 'M100 258 h44 a10 10 0 0 1 10 10 v112 a10 10 0 0 1 -10 10 h-44 a10 10 0 0 1 -10 -10 v-112 a10 10 0 0 1 10 -10 z', cx: 122, cy: 324 },
  { part: 'left-leg',   d: 'M156 258 h44 a10 10 0 0 1 10 10 v112 a10 10 0 0 1 -10 10 h-44 a10 10 0 0 1 -10 -10 v-112 a10 10 0 0 1 10 -10 z', cx: 178, cy: 324 },
]
